import { useState, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { Star, Check, X, Pencil } from 'lucide-react';

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [editing, setEditing] = useState(null); // { id, rating, title, comment }

  useEffect(() => { load(); }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reviews?status=${statusFilter}`);
      setReviews(data);
    } catch {} finally { setLoading(false); }
  };

  const setStatus = async (id, status) => {
    await api.put(`/admin/reviews/${id}`, { status });
    load();
  };

  const saveEdit = async () => {
    await api.put(`/admin/reviews/${editing.id}`, {
      rating: parseInt(editing.rating),
      title: editing.title,
      comment: editing.comment,
    });
    setEditing(null);
    load();
  };

  const del = async (id) => {
    if (!confirm('¿Eliminar esta reseña?')) return;
    await api.delete(`/admin/reviews/${id}`);
    load();
  };

  const counts = { PENDING: 0, APPROVED: 0, REJECTED: 0 };
  reviews.forEach(r => { if (counts[r.status] !== undefined) counts[r.status]++; });

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-black mb-6">Gestión de Reseñas</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[['PENDING','⏳ Pendientes'], ['APPROVED','✅ Aprobadas'], ['REJECTED','❌ Rechazadas']].map(([s, label]) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-4 py-2 rounded-full font-medium text-sm ${statusFilter === s ? 'bg-black text-white' : 'bg-gray-100'}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : reviews.length === 0 ? (
        <p className="text-center text-gray-400 py-16">No hay reseñas en este estado</p>
      ) : (
        <div className="space-y-4">
          {reviews.map(r => (
            <div key={r.id} className="bg-white border rounded-xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-semibold">{r.buyer?.name || 'Usuario'}</span>
                    <span className="text-xs text-gray-400">{r.reviewer_email}</span>
                    <div className="flex">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`w-4 h-4 ${i <= r.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">
                    Producto: <span className="font-medium text-gray-600">{r.product?.title}</span>
                    {' · '}{new Date(r.created_at).toLocaleDateString('es-CR')}
                  </p>
                  {r.title && <p className="font-medium mb-1">{r.title}</p>}
                  <p className="text-gray-600 text-sm">{r.comment}</p>
                </div>

                <div className="flex gap-2 shrink-0">
                  {r.status !== 'APPROVED' && (
                    <button onClick={() => setStatus(r.id, 'APPROVED')} title="Aprobar"
                      className="w-9 h-9 bg-green-100 hover:bg-green-200 text-green-700 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  {r.status !== 'REJECTED' && (
                    <button onClick={() => setStatus(r.id, 'REJECTED')} title="Rechazar"
                      className="w-9 h-9 bg-red-100 hover:bg-red-200 text-red-600 rounded-full flex items-center justify-center">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => setEditing({ id: r.id, rating: r.rating, title: r.title || '', comment: r.comment || '' })} title="Editar"
                    className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(r.id)} title="Eliminar"
                    className="w-9 h-9 bg-red-50 hover:bg-red-100 text-red-500 rounded-full flex items-center justify-center text-sm">
                    🗑
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">Editar Reseña</h3>
            <div className="flex gap-1 mb-4">
              {[1,2,3,4,5].map(i => (
                <button key={i} onClick={() => setEditing(e => ({ ...e, rating: i }))}>
                  <Star className={`w-7 h-7 ${i <= editing.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
            <input value={editing.title} onChange={e => setEditing(v => ({ ...v, title: e.target.value }))}
              placeholder="Título (opcional)" className="w-full border rounded-lg p-3 mb-3" />
            <textarea value={editing.comment} onChange={e => setEditing(v => ({ ...v, comment: e.target.value }))}
              rows="4" className="w-full border rounded-lg p-3 mb-4" />
            <div className="flex gap-3">
              <button onClick={() => setEditing(null)} className="flex-1 py-2 border rounded-full text-sm">Cancelar</button>
              <button onClick={saveEdit} className="flex-1 py-2 bg-primary text-white rounded-full text-sm font-semibold">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
